import React, { useEffect, useRef, useState } from 'react';
import { LoadingComponent } from '../../Loading';
import { getDocumentThumbnail } from '../../api/document';
import DocumentTable from './documentTable';
import { Button, ButtonGroup, Card, CardBody, Col, Row, Spinner } from 'reactstrap';
import styles from '../../styles/archiveui.module.css';
import { useUserContext } from '../../contexts/UserContext';
import { BulkOperationJob, Document } from '../../types/document';
import { SortField } from '../documentsearch';
import AccessControl from '../../users/userprofile/accesscontrol';

interface DocumentListProps {
  documents: Document[];
  totalCount: number;
  updatePage: (newPage: number) => void;
  startNewSearch: () => void;
  pageSize: number;
  loadingDocuments: boolean;
  currentPage?: number;
  addResultsToCart: () => void;
  showInactiveTypes: boolean;
  currentSort?: SortField;
  updateSort: (newSort: SortField) => void;
  enableBulkSelection?: boolean;
  selectDocument?: (documentid: string, selected: boolean) => void;
  selectedDocuments?: string[];
  onSelectAll?: (selected: boolean) => void;
  onSelectAllResults?: () => void;
  selectAllResultsMode?: boolean;
  excludedDocumentIds?: string[];
  effectiveSelectedCount?: number;
  activeJob?: BulkOperationJob | null;
  onBulkMoveToTrash?: () => void;
  onBulkMoveToPermanent?: () => void;
  onBulkEmptyTrash?: () => void;
  currentSearchStatus?: string;
}
const DocumentList = ({
  documents,
  totalCount,
  updatePage,
  startNewSearch,
  pageSize,
  loadingDocuments,
  currentPage,
  addResultsToCart,
  showInactiveTypes,
  currentSort,
  updateSort,
  enableBulkSelection,
  selectDocument,
  selectedDocuments,
  onSelectAll,
  onSelectAllResults,
  selectAllResultsMode,
  excludedDocumentIds,
  effectiveSelectedCount,
  activeJob,
  onBulkMoveToTrash,
  onBulkMoveToPermanent,
  onBulkEmptyTrash,
  currentSearchStatus,
}: DocumentListProps) => {
  const { currentAccount } = useUserContext();
  const tableRef = useRef<HTMLDivElement>(null);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  // Initialize banner state from sessionStorage (only hidden during current browser session)
  const bannerStorageKey = `parchment-status-banner-dismissed-${currentAccount.accountId}`;
  const [showBanner, setShowBanner] = useState<boolean>(() => {
    try {
      const dismissed = sessionStorage.getItem(bannerStorageKey);
      return dismissed !== 'true';
    } catch {
      return true;
    }
  });

  const handleDismissBanner = () => {
    setShowBanner(false);
    try {
      sessionStorage.setItem(bannerStorageKey, 'true');
    } catch {
      // Silently fail if sessionStorage is not available
    }
  };

  const fetchThumbs = async (newDocuments: Document[]) => {
    for (const document of newDocuments) {
      if (Object.keys(thumbnails).includes(document.id)) continue;

      getDocumentThumbnail(currentAccount.accountId, document.id).then((thumb) => {
        setThumbnails((thumbnails) => {
          return {
            ...thumbnails,
            [document.id]: thumb,
          };
        });
      });
    }
  };

  useEffect(() => {
    if (documents) {
      fetchThumbs(documents);
    }

    if (tableRef.current) {
      tableRef.current.scrollIntoView();
    }
  }, [documents]);

  const totalPages = Math.ceil(totalCount / pageSize);

  // Compute which documents are selected based on mode
  const computedSelectedDocuments = selectAllResultsMode
    ? documents.filter((doc) => !doc.permanent && !excludedDocumentIds?.includes(doc.id)).map((doc) => doc.id)
    : selectedDocuments || [];

  // Count permanent documents on current page
  const permanentDocsOnPage = documents.filter((doc) => doc.permanent).length;

  // Check if all current page non-permanent documents are selected
  const nonPermanentDocs = documents.filter((doc) => !doc.permanent);
  const allCurrentPageSelected = nonPermanentDocs.length > 0 && nonPermanentDocs.every((doc) => computedSelectedDocuments.includes(doc.id));
  const showSelectAllResultsBanner =
    enableBulkSelection && allCurrentPageSelected && !selectAllResultsMode && totalCount > pageSize && computedSelectedDocuments.length > 0;

  console.log('documents', documents);
  return (
    <div ref={tableRef} style={{ height: '100%' }}>
      {showBanner && (
        <div
          className="alert alert-info"
          role="alert"
          style={{ position: 'sticky', top: 0, zIndex: 1000, marginBottom: '1em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <div>
            Please check system status and maintenance updates here:{' '}
            <a href="https://status.parchment.com/" target="_blank" rel="noopener noreferrer" className="alert-link">
              Parchment Status Page
            </a>
          </div>
          <button type="button" onClick={handleDismissBanner} className={styles.bannerCloseButton} aria-label="Dismiss system status banner">
            ×
          </button>
        </div>
      )}
      <Card>
        <CardBody>
          <Row>
            <Col>
              <h3>Documents</h3>
            </Col>
            <ButtonGroup>
              <Button className={styles.scrbBtnOrange} onClick={startNewSearch}>
                New Search
              </Button>
              {totalCount < 1000 && (
                <Button color="success" onClick={addResultsToCart}>
                  Add Results to Cart
                </Button>
              )}
            </ButtonGroup>
          </Row>
          {showSelectAllResultsBanner && onSelectAllResults && (
            <Row style={{ marginTop: '1em' }}>
              <Col>
                <div
                  style={{
                    backgroundColor: '#e7f3ff',
                    padding: '0.75em',
                    borderRadius: '4px',
                    display: 'flex',
                    gap: '0.5em',
                    alignItems: 'center',
                  }}
                >
                  <span>All {documents.length} documents on this page are selected.</span>
                  <Button color="link" size="sm" onClick={onSelectAllResults} style={{ padding: '0', textDecoration: 'underline' }}>
                    Select all {totalCount} results (excluding permanent documents)
                  </Button>
                </div>
              </Col>
            </Row>
          )}
          {activeJob && (
            <Row style={{ marginTop: '1em' }}>
              <Col>
                <div
                  style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    padding: '1em',
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1em' }}>
                    <Spinner size="sm" color="warning" />
                    <div style={{ flex: 1 }}>
                      <strong>{activeJob.operation}</strong>
                      <div style={{ fontSize: '0.875em', color: '#666', marginTop: '0.25em' }}>
                        Processing {(activeJob.processedDocuments ?? 0).toLocaleString()} of {(activeJob.totalDocuments ?? 0).toLocaleString()} documents...
                      </div>
                      <div style={{ marginTop: '0.5em', height: '8px', backgroundColor: '#e0e0e0', borderRadius: '4px' }}>
                        <div
                          style={{
                            width: `${activeJob.progress ?? 0}%`,
                            height: '100%',
                            backgroundColor: '#ffc107',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </div>
                    <span style={{ fontWeight: 'bold', color: '#856404' }}>{activeJob.progress ?? 0}%</span>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          {enableBulkSelection && (effectiveSelectedCount ?? 0) > 0 && (
            <Row style={{ marginTop: '1em' }}>
              <Col>
                <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'bold' }}>
                    {effectiveSelectedCount} document{effectiveSelectedCount !== 1 ? 's' : ''} selected
                    {selectAllResultsMode && ' (all results, excluding permanent)'}
                  </span>
                  <ButtonGroup>
                    {currentSearchStatus !== 'TRASH' && onBulkMoveToTrash && (
                      <AccessControl permissionId={'trash'} action={'create'}>
                        <Button color="warning" size="sm" onClick={onBulkMoveToTrash}>
                          Move to Trash
                        </Button>
                      </AccessControl>
                    )}
                    {onBulkMoveToPermanent && (
                      <Button color="primary" size="sm" onClick={onBulkMoveToPermanent}>
                        {currentSearchStatus === 'TRASH' ? 'Move to Active' : 'Move to Permanent'}
                      </Button>
                    )}
                    {currentSearchStatus === 'TRASH' && onBulkEmptyTrash && (
                      <AccessControl permissionId={'purge'} action={'create'}>
                        <Button color="danger" size="sm" onClick={onBulkEmptyTrash}>
                          Empty Trash
                        </Button>
                      </AccessControl>
                    )}
                  </ButtonGroup>
                </div>
              </Col>
            </Row>
          )}
          {enableBulkSelection && permanentDocsOnPage > 0 && (
            <Row style={{ marginTop: '0.5em' }}>
              <Col>
                <small style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  Note: {permanentDocsOnPage} permanent document{permanentDocsOnPage !== 1 ? 's' : ''} on this page cannot be selected
                </small>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
      <LoadingComponent isLoading={loadingDocuments}>
        <DocumentTable
          documents={documents}
          accountId={currentAccount.accountId}
          thumbnails={thumbnails}
          updatePage={updatePage}
          pageSize={pageSize}
          totalPages={totalPages}
          controlledPage={currentPage}
          showInactiveTypes={showInactiveTypes}
          currentSort={currentSort}
          updateSort={updateSort}
          enableBulkSelection={enableBulkSelection}
          selectDocument={selectDocument}
          selectedDocuments={computedSelectedDocuments}
          onSelectAll={onSelectAll}
        />
      </LoadingComponent>
    </div>
  );
};

export default DocumentList;
